<template>
  <div>
    <div class="full-page" v-if="linePdf">
      <b-icon-x style="z-index: 1000; position: relative;" class="close-icon" @click="closePage('linePdf')"></b-icon-x>
      <div class="container-fluid">
        <h3>Line PDF</h3>
        <b-button class="mb-1 mr-1" style="float: left;" size="sm" @click="createPdf">Download PDF</b-button>
        <b-button class="mb-1 mr-1" style="float: left;" size="sm" @click="createPNG">Download PNG</b-button>
        <b-button class="mb-1 mr-1" style="float: left;" size="sm" @click="showSettings = !showSettings">PDF Settings</b-button>

        <div id="svg-cont" class="container">
          <svg id="svg-line-top" class="pdf-page" :viewBox="`0 0 ${700} ${989}`">
            <!-- svg logo -->
            <g transform="scale(0.26)translate(58,58)">
              <path :d="svgLogo.logoPath1" style="fill:#2fade2"></path>
              <path :d="svgLogo.logoPath2" style="fill:#2c2840"></path>
            </g>

            <!-- header info -->
            <text style="font: bold 8px sans-serif;" :x="col1" :y="rows(1)">Name:</text>
            <text style="font: 8px sans-serif;" :x="col2" :y="rows(1)">{{ currentInfo.businessName }}</text>

            <text style="font: bold 8px sans-serif;" :x="col1" :y="rows(2)">Address:</text>
            <text style="font: 8px sans-serif;" :x="col2" :y="rows(2)">{{ currentInfo.street }}</text>
            <text style="font: 8px sans-serif;" :x="col1" :y="rows(3)">
              {{ `${currentInfo.city || ""} ${currentInfo.state || ""} ${currentInfo.postalCode || ""}` }}
            </text>

            <!-- test title info  -->
            <text style="font: 9px sans-serif;" :x="270" :y="rows(4)">
              {{ `${currentInfo.locationName || ""} - ${currentInfo.plotName || ""} - ${currentInfo.varietyName || ""}` }}
            </text>

            <!-- legend -->
            <SvgLegend :x="510" :y="28" />

            <!-- header -->
            <rect x="18" y="95" width="660" height="14" fill="#0E7FAD" />
            <text style="font: bold 8px sans-serif;" fill="white" x="20" y="105">Element</text>

            <text v-for="d of dateLines" :key="d.id" style="font: 6.5px sans-serif;" :x="d.x" y="90" :transform="`rotate(-45 ${d.x} 90)`">
              {{ d.text }}
            </text>
            <g v-if="showMoon">
              <g
                :transform="`scale(.08)translate(${d.x / 0.08 - 180},1040)`"
                v-for="(d, i) of dateLines"
                :key="`${i}-moon`"
                v-html="moonIcon(d).icon"
              ></g>
            </g>

            <!-- elements -->
            <g v-for="(elmt, i) of firstPageElements" :key="elmt.key" :transform="`translate(0,${graphRows(i)})`">
              <rect x="150" :y="ranges(elmt.key).veryy" width="528" :height="ranges(elmt.key).veryh" fill="#e6f1fd" />
              <rect x="150" :y="ranges(elmt.key).oky" width="528" :height="ranges(elmt.key).okh" fill="#c5e1ff" />
              <line
                x1="135"
                x2="678"
                :y1="ranges(elmt.key).perfect"
                :y2="ranges(elmt.key).perfect"
                style="stroke:black;stroke-width: .5"
                stroke-dasharray="2,4"
              />

              <text style="font: bold 10px sans-serif;" x="25" y="40">{{ elmt.label }}</text>

              <text style="font: bold 6px sans-serif;" x="115" :y="minMax(elmt.key).y + 2">{{ minMax(elmt.key).val }}</text>

              <line x1="18" x2="678" y1="76" y2="76" style="stroke:#888;stroke-width:.5" />
              <line x1="18" x2="678" y1="78" y2="78" style="stroke:#888;stroke-width:.5" />

              <line
                v-for="d of dateLines"
                :key="`${d.id}-${i}`"
                :x1="d.x"
                :x2="d.x"
                y1="0"
                y2="80"
                style="stroke:#ccc;stroke-width:.5"
                stroke-dasharray="4,5"
              />

              <path v-for="path of elementLine(elmt.key)" :key="path.id" :style="path.style" fill="none" stroke-width="2" :d="path.data" />

              <g v-for="c of elementCircles(elmt.key)" :key="c.id">
                <rect
                  :x="c.circleX + textOffset(c.plantPart, c.text)"
                  :y="c.circleY - 6.5"
                  height="8"
                  :width="c.text.length * 5"
                  fill="white"
                  style="opacity: .5"
                />
                <circle :cx="c.circleX" :cy="c.circleY" r="4.5" :fill="c.color" style="stroke:black;stroke-width:.5"></circle>
                <text style="font: bold 7px sans-serif;" :x="c.circleX + textOffset(c.plantPart, c.text)" :y="c.circleY">{{ c.text }}</text>
              </g>
            </g>

            <text
              v-for="ann of annotationsTop"
              :key="ann.id"
              class="draggable"
              :style="`font: ${ann.fontSize}px sans-serif;`"
              :fill="ann.color"
              x="180"
              y="180"
            >
              {{ ann.text }}
            </text>

            <!-- disclaimer  -->
            <PdfDisclaimer :x="18" :y="960" />
          </svg>
        </div>

        <div v-show="twoPage" id="svg-cont-2" class="container">
          <svg id="svg-line-bottom" class="pdf-page" :viewBox="`0 0 ${700} ${989}`">
            <!-- header -->
            <rect x="18" y="39" width="660" height="14" fill="#0E7FAD" />
            <text style="font: bold 8px sans-serif;" fill="white" x="20" y="49">Element</text>

            <text v-for="d of dateLines" :key="d.id" style="font: 6.5px sans-serif;" :x="d.x" y="34" :transform="`rotate(-45 ${d.x} 34)`">
              {{ d.text }}
            </text>
            <g
              v-show="showMoon"
              :transform="`scale(.08)translate(${d.x / 0.08 - 180},320)`"
              v-for="(d, i) of dateLines"
              :key="`${i}-moon`"
              v-html="moonIcon(d).icon"
            ></g>

            <!-- elements -->
            <g v-for="(elmt, i) of secondPageElements" :key="elmt.key" :transform="`translate(0,${graphRows(i, 'noHead')})`">
              <rect x="150" :y="ranges(elmt.key).veryy" width="528" :height="ranges(elmt.key).veryh" fill="#e6f1fd" />
              <rect x="150" :y="ranges(elmt.key).oky" width="528" :height="ranges(elmt.key).okh" fill="#c5e1ff" />
              <line
                x1="135"
                x2="678"
                :y1="ranges(elmt.key).perfect"
                :y2="ranges(elmt.key).perfect"
                style="stroke:black;stroke-width: .5"
                stroke-dasharray="2,4"
              />

              <text style="font:  bold 10px sans-serif;" x="25" y="40">{{ elmt.label }}</text>

              <text style="font: bold 6px sans-serif;" x="115" :y="minMax(elmt.key).y + 2">{{ minMax(elmt.key).val }}</text>

              <line x1="18" x2="678" y1="76" y2="76" style="stroke:#888;stroke-width:.5" />
              <line x1="18" x2="678" y1="78" y2="78" style="stroke:#888;stroke-width:.5" />

              <line
                v-for="d of dateLines"
                :key="`${d.id}-${i}`"
                :x1="d.x"
                :x2="d.x"
                y1="0"
                y2="80"
                style="stroke:#ccc;stroke-width:.5"
                stroke-dasharray="4,5"
              />

              <path v-for="path of elementLine(elmt.key)" :key="path.id" :style="path.style" fill="none" stroke-width="2" :d="path.data" />

              <g v-for="c of elementCircles(elmt.key)" :key="c.id">
                <rect
                  :x="c.circleX + textOffset(c.plantPart, c.text)"
                  :y="c.circleY - 6.5"
                  height="8"
                  :width="c.text.length * 5"
                  fill="white"
                  style="opacity: .5"
                />
                <circle :cx="c.circleX" :cy="c.circleY" r="4.5" :fill="c.color" style="stroke:black;stroke-width:.5"></circle>
                <text style="font: bold 7px sans-serif;" :x="c.circleX + textOffset(c.plantPart, c.text)" :y="c.circleY">{{ c.text }}</text>
              </g>
            </g>

            <text
              v-for="ann of annotationsBottom"
              :key="ann.id"
              class="draggable"
              :style="`font: ${ann.fontSize}px sans-serif;`"
              :fill="ann.color"
              x="180"
              y="180"
            >
              {{ ann.text }}
            </text>

            <!-- disclaimer  -->
            <PdfDisclaimer :x="18" :y="965" />
          </svg>
        </div>

        <div style="padding: 4em;"></div>

        <div class="pdf-sidebar-outer" v-show="showSettings">
          <div class="pdf-sidebar">
            <div class="pdf-inner">
              <b-icon-chevron-right class="close-settings-icon" @click="showSettings = false"></b-icon-chevron-right>
              <h3>PDF Settings</h3>
              <b-form style="text-align: left">
                <b-form-group>
                  <b-form-checkbox v-model="twoPage" switch>Two page PDF</b-form-checkbox>
                </b-form-group>

                <b-form-group>
                  <b-form-checkbox v-model="showMoon" switch>Show Moonphase</b-form-checkbox>
                </b-form-group>

                <PDFAnnotations name="line-top" button-text="Annotate Top Page" />
                <div class="mt-2"></div>
                <PDFAnnotations name="line-bottom" button-text="Annotate Bottom Page" />

                <div class="mt-4"></div>
                <b-button v-show="orderHasChanged" size="sm" @click="resetOrder"> Reset </b-button>

                <draggable v-model="elementList" @start="drag = true" @end="drag = false" v-bind="dragOptions" group="elements">
                  <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                    <div class="list-item" v-for="elmt of elementList" :key="elmt.id">
                      <b-form-checkbox v-model="elmt.active">{{ elmt.label }}</b-form-checkbox>
                    </div>
                  </transition-group>
                </draggable>
              </b-form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { line, scaleLinear, scaleTime } from "d3";
import draggable from "vuedraggable";
import { mapGetters, mapMutations } from "vuex";
import { logoPath1, logoPath2 } from "../assets/images/CHLlogo";
import { pdf } from "../modules/api";
import { downloadSvg, dynamicSortDate, dynamicSortNumber, makeDraggableSvg, randomId } from "../modules/utils";

import SvgLegend from "../components/SvgLegend";
import { EventBus } from "../modules/eventBus";
import { lunarPhaseAndIcon } from "../modules/moonphase";
import PDFAnnotations from "./PDFAnnotations.vue";
import PdfDisclaimer from "./PdfDisclaimer.vue";

export default {
  name: "line-pdf",
  data() {
    return {
      windowWidth: 0,
      windowHeight: 0,
      col1: 100,
      col2: 150,
      elements: [
        { id: 1, active: true, key: "sugar", label: "Total Sugars" },
        { id: 2, active: true, key: "pH", label: "pH" },
        { id: 3, active: true, key: "EC", label: "EC" },
        { id: 4, active: true, key: "potassium", label: "Potassium" },
        { id: 5, active: true, key: "calcium", label: "Calcium" },
        { id: 6, active: true, key: "magnesium", label: "Magnesium" },
        { id: 7, active: true, key: "sodium", label: "Sodium" },
        { id: 8, active: true, key: "ammonium", label: "Ammonium" },
        { id: 9, active: true, key: "NO3_nitrate", label: "Nitrate" },
        { id: 10, active: true, key: "nitrogen_in_nitrate", label: "N in Nitrate" },
        { id: 11, active: true, key: "nitrogen", label: "Total Nitrogen" },
        { id: 12, active: true, key: "chloride", label: "Chloride" },
        { id: 13, active: true, key: "sulfur", label: "Sulfur" },
        { id: 14, active: true, key: "phosphorous", label: "Phosphorus" },
        { id: 15, active: true, key: "silica", label: "Silica" },
        { id: 16, active: true, key: "iron", label: "Iron" },
        { id: 17, active: true, key: "manganese", label: "Manganese" },
        { id: 18, active: true, key: "zinc", label: "Zinc" },
        { id: 19, active: true, key: "boron", label: "Boron" },
        { id: 20, active: true, key: "copper", label: "Copper" },
        { id: 21, active: true, key: "molybdenum", label: "Molybdenum" },
        { id: 22, active: true, key: "aluminium", label: "Aluminium" },
        { id: 23, active: true, key: "cobalt", label: "Cobalt" },
      ],
      showSettings: false,
      twoPage: true,
      drag: false,
      orderHasChanged: false,
      annotationsTop: [],
      annotationsBottom: [],
      showMoon: false,
    };
  },
  methods: {
    async createPdf() {
      let svg = document.getElementById("svg-cont").innerHTML;
      let svg2 = document.getElementById("svg-cont-2").innerHTML;
      let svgArr = [svg];
      if (this.twoPage) {
        svgArr.push(svg2);
      }

      let res = await pdf.createLinePdf({ svgs: svgArr });

      const blob = new Blob([res.data], { type: "application/pdf" });

      // process to auto download it
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      document.body.append(link);
      link.href = fileURL;
      link.download = `${this.currentInfo.businessName}-${this.currentInfo.locationName}-${
        this.currentInfo.plotName
      }-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      setTimeout(function() {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    },
    async createPNG() {
      this.showMoon = false;
      await this.$nextTick();
      let fileName = `${this.currentInfo.businessName}-${this.currentInfo.locationName}-${this.currentInfo.plotName}-${new Date()
        .toISOString()
        .slice(0, 10)}.png`;
      let svg = document.getElementById("svg-cont");
      downloadSvg(svg.firstChild, fileName);
      if (this.twoPage) {
        let svg2 = document.getElementById("svg-cont-2");
        downloadSvg(svg2.firstChild, fileName);
      }
    },
    onResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;
    },
    textOffset(plantPart, text) {
      if (plantPart === "young") {
        let offset = text.length * 4.2;
        return -7 - offset;
      }
      return 5;
    },
    elementLine(element) {
      const { x, y, old, young } = this.scaleAndData(element);

      return [
        {
          data: line()
            .x((d, i) => x(d.date))
            .y(d => y(parseFloat(d.value)))(young),
          id: randomId(),
          style: { stroke: "lightgreen" },
        },
        {
          data: line()
            .x((d, i) => x(d.date))
            .y(d => y(parseFloat(d.value)))(old),
          id: randomId(),
          style: { stroke: "darkgreen" },
        },
      ];
    },
    elementCircles(element) {
      const { x, y, old, young, range } = this.scaleAndData(element);

      let data = [];
      for (let val of old) {
        data.push({
          circleX: x(val.date),
          circleY: y(val.value),
          text: val.value,
          color: this.selectColor(val.value, range),
          plantPart: "old",
        });
      }
      for (let val of young) {
        data.push({
          circleX: x(val.date),
          circleY: y(val.value),
          text: val.value,
          color: this.selectColor(val.value, range),
          plantPart: "young",
        });
      }

      return data;
    },
    ranges(element) {
      const range = this.currentSeriesRange(element);
      const { x, y } = this.scaleAndData(element);

      return {
        perfect: y(range.Perfect),
        oky: y(range["Ok High"]),
        okh: y(range["Ok Low"]) - y(range["Ok High"]),
        veryy: y(range["Very High"]),
        veryh: y(range["Very Low"]) - y(range["Very High"]),
      };
    },
    minMax(element) {
      const range = this.currentSeriesRange(element);
      const { y } = this.scaleAndData(element);

      return { y: y(range.Perfect), val: range.Perfect };
    },
    scaleAndData(element) {
      const range = this.currentSeriesRange(element);

      let young = this.allRecords.filter(x => x.plantPart.includes("young"));
      let old = this.allRecords.filter(x => x.plantPart.includes("old"));

      function testMap(x) {
        let v = x.values.find(d => d.name == element);
        let value = v ? v.value : 0;
        return { value, date: new Date(x.sampleDate) };
      }
      young = young.map(testMap).sort(dynamicSortDate("date"));
      old = old.map(testMap).sort(dynamicSortDate("date"));

      let data = old.concat(young);
      const dates = data.map(x => x.date);

      const x = scaleTime()
        .domain([Math.min(...dates), Math.max(...dates)])
        .range([160, 665]);
      let values = data.map(x => parseFloat(x.value));

      if (range["Very Low"] && range["Very High"]) {
        values = values.concat([range["Very Low"], range["Very High"]]);
      }

      const y = scaleLinear()
        .domain([Math.max(...values), Math.min(...values)])
        .range([10, 70]);

      return { x, y, old, young, range };
    },
    currentSeriesRange(element) {
      if (!this.allRecords[0]) {
        return {};
      }
      const crop = this.allRecords[0].cropName;
      const ranges = this.getRanges.find(x => x.cropName == crop);
      const elmtRange = {};
      if (ranges) {
        for (let name of ranges.names) {
          elmtRange[name] = ranges[name][element];
        }
      }
      return elmtRange;
    },
    selectColor(value, ranges, colorBook = "muted") {
      let color = this.colors[colorBook].young[0];
      if (value < ranges["Ok Low"]) {
        color = this.colors[colorBook].young[1];
      }
      if (value < ranges["Very Low"]) {
        color = this.colors[colorBook].young[2];
      }
      if (value > ranges["Ok High"]) {
        color = this.colors[colorBook].young[3];
      }
      if (value > ranges["Very High"]) {
        color = this.colors[colorBook].young[4];
      }

      return color;
    },
    resetOrder() {
      this.elements.sort(dynamicSortNumber("id"));
      this.orderHasChanged = false;
    },
    moonIcon(d) {
      return lunarPhaseAndIcon(new Date(d.text));
    },
    ...mapMutations(["closePage"]),
  },
  computed: {
    currentInfo() {
      if (!this.allRecords || !this.allRecords[0]) {
        return {};
      }
      let address = this.addresses.find(x => x.businessId === this.allRecords[0].businessId);

      if (address && address.address.length > 1) {
        let billing = address.address.find(x => x.type === "billing");
        if (billing) {
          address.address[0] = billing;
        }
      }
      return {
        ...address.address[0],
        businessName: this.allRecords[0].businessName,
        locationName: this.allRecords[0].locationName,
        plotName: this.allRecords[0].plotName,
        cropName: this.allRecords[0].cropName,
        varietyName: this.allRecords[0].varietyName,
      };
    },
    rows() {
      return function(rowNum) {
        let start = 15;
        let rowPx = 10;
        return rowNum * rowPx + start;
      };
    },
    graphRows() {
      return function(rowNum, x) {
        let start = x === "noHead" ? 53 : 109;
        let rowPx = 75;
        return rowNum * rowPx + start;
      };
    },
    dateLines() {
      const { x, old, young } = this.scaleAndData("sugar");
      let data = old.concat(young);
      const dates = Array.from(new Set(data.map(x => x.date.toLocaleDateString())));
      let dateList = [];

      for (let date of dates) {
        dateList.push({
          x: x(new Date(date)),
          text: date,
          id: randomId(),
        });
      }

      return dateList;
    },
    svgLogo() {
      return { logoPath1, logoPath2 };
    },
    colors() {
      return {
        default: {
          young: ["lightgreen", "gold", "#fb8181", "#54bbf7", "purple"],
          old: ["darkgreen", "#ebb400", "red", "#0099ff", "#5d0e5d"],
        },
        simple: {
          young: ["lightgreen", "lightgreen", "lightgreen", "lightgreen", "lightgreen"],
          old: ["darkgreen", "darkgreen", "darkgreen", "darkgreen", "darkgreen"],
        },
        muted: {
          young: ["#BCFFB3", "gold", "#FFB3B3", "#B3FFFF", "#D9B3FF"],
          old: ["#8FFF80", "#ebb400", "#FF8080", "#80FFFF", "#BF80FF"],
        },
        mutedsame: {
          young: ["#BCFFB3", "gold", "#FFB3B3", "#B3FFFF", "#D9B3FF"],
          old: ["#BCFFB3", "gold", "#FFB3B3", "#B3FFFF", "#D9B3FF"],
        },
      };
    },
    firstPageElements() {
      let activeElmts = this.elements.filter(x => x.active);
      return activeElmts.slice(0, 11);
    },
    secondPageElements() {
      let activeElmts = this.elements.filter(x => x.active);
      return activeElmts.slice(11, 23);
    },
    elementList: {
      get() {
        return this.elements;
      },
      set(list) {
        this.orderHasChanged = true;
        this.elements = list;
      },
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
      };
    },
    ...mapGetters({
      linePdf: "linePdf",
      addresses: "currentAddresses",
      allRecords: "allRecords",
      getRanges: "getRanges",
    }),
  },
  components: {
    draggable,
    SvgLegend,
    PdfDisclaimer,
    PDFAnnotations,
  },
  watch: {
    async linePdf(value) {
      if (value) {
        await this.$nextTick();
        makeDraggableSvg(this.$el.querySelector("#svg-line-top"));
        makeDraggableSvg(this.$el.querySelector("#svg-line-bottom"));
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.onResize();

    EventBus.$on("update-annotations", data => {
      if (data.name === "line-top") {
        this.annotationsTop = data.annotations;
      } else if (data.name === "line-bottom") {
        this.annotationsBottom = data.annotations;
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
};
</script>

<style lang="scss" scoped>
.draggable {
  cursor: grab;
}

.pdf-page {
  box-shadow: 0px 0px 5px #484848;
  margin-bottom: 5px;
}

.logo-scale {
  transform: scale(0.3);
}

.pdf-sidebar-outer {
  position: fixed;
  right: 0;
  top: 0;
  width: 20em;
  height: 100%;
}

.pdf-sidebar {
  position: relative;
  padding-top: 3.5em;
  background-color: rgb(253, 247, 239);
  height: 100%;
  overflow: auto;
  box-shadow: 0px 0px 5px #484848;
}

.pdf-inner {
  position: relative;
  margin: 1em;
  padding-bottom: 4em;
}

.close-settings-icon {
  font-size: 1.8em;
  color: #7a7a7a;
  float: left;
  cursor: pointer;
  margin-top: -0.2em;
  margin-right: -1em;
}

.list-item {
  padding: 0.3em;
  margin: 0.5em;
  background-color: white;
  text-align: left;
  box-shadow: 0px 0px 2px #7a7a7a;
  border-radius: 5px;
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}
</style>
